import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import ReactFullpage from '@fullpage/react-fullpage'
import { Link } from 'gatsby'
import { rgba, stripUnit } from 'polished'
import { SLIDESHOW_SPEED } from 'utils/constants'

import { white, black, grey, yellow } from 'styles/colors'
import { borderRadius, navHeightMobile, navHeight, square } from 'styles/global'
import * as spacing from 'styles/spacing'
import { H1, H2, H4, fontSizes, fontWeights, lineHeights } from 'styles/typography'
import { media, mediaDown } from 'styles/media'

import NativeAuthContext from 'components/NativeAuth'
import { ScrollConsumer } from 'components/ScrollContext'
import SlideshowMenu from 'components/slideshow/SlideshowMenu'
import Portal from 'components/Portal'
import Icon from 'components/Icon'
import Header from 'components/Header'
import HealthWarning from 'components/HealthWarning'
import Details from 'components/slideshow/Details'
import ProductCard from 'components/slideshow/ProductCard'
import FlavourSlider from 'components/slideshow/FlavourSlider'
import SwatchList from 'components/slideshow/SwatchList'

import DownChevronLarge from 'icons/down-chevron-large.svg'
import CloseIcon from 'icons/close.svg'

const Container = styled.div`
  position: relative;
  background-image: linear-gradient(71deg, #000000 0%, #131922 100%);
  min-height: 100vh;
`

const Section = styled.div`
  position: relative;
`

const FixedHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${navHeight};
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 100;

  ${mediaDown.medium`
    display: none;
  `};

  .fp-viewing-step-1 & {
    opacity: 0;
    visibility: hidden;
  }
`

const Heading = styled(H1)`
  margin-bottom: 0.5em;
  color: ${white};
  font-family: 'Trim Poster';
`

const MenuWrap = styled.div`
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;

  .fp-viewing-step-1 & {
    opacity: 0;
    visibility: hidden;
  }
`

const Subheading = styled(H2)`
  margin-bottom: 0.5em;
  font-family: 'Trim Poster';
`

const Lead = styled(H4)`
  color: ${white};
  line-height: ${lineHeights.base};

  ${media.large`
    font-size: 22px;
  `};

  ${media.xLarge`
    font-size: 24px;
  `};
`

const Fade = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  height: 35%;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #060808 100%);
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  opacity: 0;
  visibility: hidden;
  z-index: 20;
  pointer-events: none;
  /* display: none; */

  .fp-viewing-step-2 &,
  .fp-viewing-step-3 &,
  .fp-viewing-step-4 & {
    opacity: 1;
    visibility: visible;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 30;
  pointer-events: none;

  ${mediaDown.medium`
    text-align: center;
  `};

  ${H1}, ${H2} {
    color: ${white};
  }

  p {
    color: ${rgba(white, 0.75)};
  }
`

const StyledLink = styled(Link)`
  color: ${white};
  text-decoration: underline;
  font-weight: ${fontWeights.medium};
  pointer-events: auto;
`

const ShopLink = styled.button`
  height: ${navHeightMobile};
  position: fixed;
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  color: ${white};
  text-transform: uppercase;
  top: 0;
  right: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  font-size: ${fontSizes.small};
  letter-spacing: 0.1em;
  padding: 0 ${spacing.large};

  .fp-viewing-step-1 & {
    opacity: 0;
    visibility: hidden;
  }
`

const Step1HeaderWrap = styled.div`
  position: relative;
  height: ${navHeightMobile};

  ${media.medium`
    height: ${navHeight};
  `};
`

const Step1Content = styled(Content)`
  position: relative;
  width: 100vw;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Step1DownArrow = styled.button`
  margin: ${spacing.large} 0;
  color: ${white};
  pointer-events: auto;

  ${media.small`
    margin: ${spacing.xLarge} 0;
  `};

  ${media.medium`
    margin: ${spacing.xxLarge} 0;
  `};

  ${media.large`
    margin: 56px 0;
  `};

  ${media.xLarge`
    margin: 64px 0;
  `};
`

const Step1ContentInner = styled.div`
  width: 100%;
  padding: 0 ${spacing.medium};
  margin-left: auto;
  margin-right: auto;

  ${media.small`
    padding: 0 10%;
  `};

  ${media.medium`
    width: 100%;
    padding: 0 5%;
    max-width: 1200px;
  `};

  ${media.large`
    max-width: 1300px;
  `};

  ${media.xLarge`
    max-width: 1600px;
  `};
`

const Step1List = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: -${stripUnit(spacing.tiny) * 1.5 + 'px'};

  ${media.small`
    margin: -${spacing.small};
  `};

  ${media.medium`
    justify-content: space-between;
  `};

  ${media.large`
    margin: -${stripUnit(spacing.small) * 1.5 + 'px'};
  `};
`

const Step1ListItem = styled.div`
  padding: ${stripUnit(spacing.tiny) * 1.5 + 'px'};
  width: 50%;
  display: flex;

  ${media.medium`
    width: 25%;
    padding: ${spacing.small};
  `};

  ${media.large`
    padding: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};
`

const Step1Heading = styled(Subheading)`
  font-family: 'Trim Poster';
  margin-bottom: 0.66em;
  text-align: center;
  font-size: 32px;

  @supports (-webkit-text-stroke: 1px black) {
    -webkit-text-stroke: 0.02em ${white};
    -webkit-text-fill-color: transparent;
  }

  ${media.small`
    font-size: 48px;
  `};

  ${media.medium`
    font-size: 64px;
  `};

  ${media.large`
    font-size: 80px;
  `};

  ${media.xLarge`
    font-size: 100px;
  `};
`

const Step2DeviceWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: 6.25vw;
  min-width: 72px;
  max-width: 108px;
  transform: translate3d(-50%, -50%, 0);
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  z-index: 10;
  pointer-events: none;

  .fp-viewing-step-1 & {
    opacity: 0;
  }

  img {
    width: 100%;
  }

  .fp-viewing-step-3 & {
    transform: translate3d(-50%, 5%, 0) scale(1.75);
  }

  .fp-viewing-step-4 & {
    transform: translate3d(-50%, -35%, 0) scale(1.75);
  }

  .fp-viewing-step-5 & {
    transform: translate3d(-50%, -50%, 0) scale(0.85);
  }

  .fp-viewing-step-6 &,
  .fp-viewing-step-7 &,
  .fp-viewing-step-8 &,
  .fp-viewing-step-9 & {
    opacity: 0;
    transform: translate3d(-50%, -50%, 0) scale(0.8);
  }
`

const Step2SwatchWrap = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 10vh;
  display: flex;
  justify-content: center;
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  opacity: 0;
  visibility: hidden;
  z-index: 30;
  pointer-events: auto;

  ${media.small`
    bottom: 60px;
  `};

  ${media.medium`
    position: static;
    transform: none;
    left: 0;
    justify-content: flex-start;
  `};

  .fp-viewing-step-2 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step2Content = styled(Content)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 10%;
  `};

  ${media.medium`
    position: absolute;
    top: 50%;
    width: 25%;
    height: auto;
    left: 62.5%;
    transform: translateY(-50%);
    max-width: 420px;
  `};
`

const Step2MobileDevice = styled.img`
  margin: ${spacing.large} auto ${spacing.large};
  width: 320px;

  ${media.small`
    width: 400px;
    margin: ${spacing.large} auto ${spacing.xLarge};
  `};
`

const Step2Heading = styled(Heading)`
  position: static;

  ${mediaDown.small`
    margin-top: -${spacing.xxLarge};
  `};

  ${mediaDown.medium`
    margin-bottom: 0;
  `};
`

const Step2Subheading = styled(Lead)`
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;

  ${media.medium`
    margin-bottom: ${spacing.large};
  `};

  ${media.large`
    margin-left: 0;
    margin-bottom: ${spacing.xLarge};
    max-width: 330px;
  `};
`

const Step3Content = styled(Content)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 10%;
  `};

  ${media.medium`
    position: absolute;
    top: 50%;
    left: 65%;
    width: 25%;
    height: auto;
    transform: translateY(-50%);
    max-width: 400px;
  `};

  .fp-viewing-step-3 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step3ContentWrap = styled.div`
  position: relative;

  ${mediaDown.medium`
    padding-top: ${navHeightMobile};
    max-width: 380px;
    margin-left: auto;
    margin-right: auto;
  `};
`

const Step3Line = styled.div`
  display: block;
  position: absolute;
  height: 13vh;
  width: 3px;
  top: 60px;
  background-color: ${white};
  right: 30px;

  ${media.medium`
    position: fixed;
    width: 11vw;
    height: 3px;
    right: 35vw;
    top: 40vh;
    margin-right: 3vw;
    opacity: 0;
    visibility: hidden;
    transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;
    transition-delay: 0.5s;
    z-index: 40;

    .fp-viewing-step-3 & {
      opacity: 1;
      visibility: visible;
    }
  `};

  ${media.large`
    top: 43vh;
  `};

  &:before {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border: 3px solid ${white};
    border-radius: 50%;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);

    ${media.medium`
      right: 100%;
      left: auto;
      bottom: auto;
      transform: translateY(-50%);
    `};
  }
`

const Step3MobileDevice = styled.div`
  position: relative;
  display: block;
  margin-left: -40%;
  margin-right: 20%;
  margin-bottom: 15vh;

  ${media.medium`
    display: none;
  `};
`

const Step4Content = styled(Content)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 10%;
  `};

  ${media.medium`
    position: absolute;
    top: auto;
    bottom: 23%;
    width: 25%;
    height: auto;
    left: 65%;
    max-width: 400px;
  `};

  .fp-viewing-step-4 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step4ContentWrap = styled.div`
  position: relative;

  ${mediaDown.medium`
    padding-top: ${navHeightMobile};
    max-width: 380px;
    margin-left: auto;
    margin-right: auto;
  `};
`

const Step4Line = styled.div`
  display: block;
  position: absolute;
  height: 12vh;
  width: 3px;
  top: 100%;
  margin-top: -20px;
  margin-left: -20px;
  background-color: ${white};
  left: 50%;
  opacity: 0;
  visibility: hidden;
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;
  transition-delay: 0.5s;

  ${media.medium`
    position: fixed;
    right: 35vw;
    top: auto;
    bottom: 40vh;
    height: 3px;
    left: auto;
    width: 9vw;
    margin-right: 3vw;
    margin-top: 0;
    margin-left: 0;
    z-index: 40;
  `};

  .fp-viewing-step-4 & {
    opacity: 1;
    visibility: visible;
  }

  &:before {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border: 3px solid ${white};
    border-radius: 50%;
    bottom: 100%;
    transform: translateX(-50%);

    ${media.medium`
      right: 100%;
      bottom: auto;
      transform: translateY(-50%);
    `};
  }
`

const Step4MobileDevice = styled.div`
  position: relative;
  display: block;
  margin-left: -19%;
  margin-right: -30%;
  margin-bottom: 15vh;

  ${media.medium`
    display: none;
  `};
`

const Step5Content = styled(Content)`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${mediaDown.medium`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 10%;
  `};

  ${media.medium`
    position: absolute;
    height: auto;
    top: 50%;
    width: 22%;
    left: 60%;
    transform: translateY(-50%);
  `};

  .fp-viewing-step-5 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step5Size = styled.div`
  position: fixed;
  right: 60%;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  visibility: hidden;
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  transition-delay: 0.5s;

  .fp-viewing-step-5 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step5SizeLabel = styled.span`
  background-color: ${white};
  color: ${black};
  font-size: ${fontSizes.tiny};
  padding: 7px ${spacing.small} 6px;
  border-radius: ${borderRadius};
  font-weight: ${fontWeights.bold};
`

const Step5Line = styled.div`
  position: fixed;
  right: 57.5%;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 27vw;
  min-height: 310px;
  background-color: ${white};
  opacity: 0;
  visibility: hidden;
  transition: all ${SLIDESHOW_SPEED} ease-in-out;
  transition-delay: 0.5s;

  .fp-viewing-step-5 & {
    opacity: 1;
    visibility: visible;
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    height: 2px;
    background-color: ${white};
    width: 24px;
    left: 0;
  }

  &:before {
    top: 0;
  }

  &:after {
    bottom: 0;
  }
`

const Step5MobileDeviceWrap = styled.div`
  position: relative;
  margin-left: auto;
  margin-right: auto;
  width: 56px;
  margin-bottom: ${spacing.xLarge};

  ${media.small`
    margin-bottom: ${spacing.xxLarge};
    width: 60px;
  `};
`

const Step5MobileLine = styled.div`
  position: absolute;
  top: 0;
  left: -${spacing.xxLarge};
  width: 2px;
  height: 100%;
  background-color: ${white};

  &:before,
  &:after {
    content: '';
    position: absolute;
    height: 2px;
    background-color: ${white};
    width: 24px;
    left: 0;
  }

  &:before {
    top: 0;
  }

  &:after {
    bottom: 0;
  }
`

const Step5MobileSizeLabel = styled(Step5SizeLabel)`
  position: absolute;
  top: 50%;
  right: 100%;
  white-space: nowrap;
  transform: translateY(-50%);
  margin-right: 64px;
`

const Step6Content = styled(Content)`
  position: absolute;

  ${mediaDown.medium`
    top: ${navHeightMobile};
    left: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 10%;
  `};

  ${media.medium`
    top: 50%;
    left: 62.5%;
    max-width: 280px;
    transform: translateY(-50%);
  `};

  ${media.large`
    max-width: 350px;
  `};

  ${media.xLarge`
    max-width: 390px;
  `};

  .fp-viewing-step-6 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step6ContentInner = styled.div`
  position: static;
  text-align: center;

  ${mediaDown.medium`
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  `};

  ${media.xLarge`
    margin-right: -60px;
  `};
`

const Step6DeviceWrap = styled.div`
  position: relative;
  width: 48px;
  margin-bottom: -${spacing.xLarge};

  ${mediaDown.medium`
    margin-left: auto;
    margin-right: auto;
  `};

  ${media.small`
    width: 56px;
  `};

  ${media.medium`
    position: fixed;
    top: 52.5%;
    left: 35%;
    width: 7.5vw;
    min-width: 80px;
    margin-bottom: 0;
    transform: translateY(-40%);
    transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;
    opacity: 0;
    visibility: hidden;
    z-index: 10;
  `};

  img {
    width: 100%;
  }

  .fp-viewing-step-6 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step6Strength = styled.div`
  display: flex;
  margin-bottom: ${spacing.large};
  justify-content: center;

  ${media.medium`
    margin-top: ${spacing.xxLarge};
    margin-bottom: ${spacing.xLarge};
  `};
`

const Step6Button = styled.button`
  color: ${white};
  text-decoration: underline;
  font-weight: ${fontWeights.medium};
  pointer-events: auto;
`

const Step6MobileContent = styled.div`
  position: fixed;
  top: ${navHeightMobile};
  left: 0;
  width: 100%;
  bottom: 0;
  padding-top: 30%;
  z-index: 30;
  text-align: center;
  color: ${white};
  transition: opacity ${SLIDESHOW_SPEED} ease-in-out, visibility ${SLIDESHOW_SPEED} ease-in-out;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(71deg, #000000 0%, #131922 100%);
    opacity: 0.93;
    pointer-events: none;
  }
`

const Step6MobileContentInner = styled.div`
  position: relative;
  z-index: 30;
`

const Step6MobileClose = styled.button`
  color: ${white};
  margin-top: ${spacing.xxLarge};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${rgba(grey, 0.75)};
  border-radius: 50%;
  margin-left: auto;
  margin-right: auto;
  ${square('48px')};

  ${media.small`
    margin-top: ${spacing.xxxLarge};
  `};
`

const Strength = styled.div`
  position: relative;
  text-align: center;

  & + & {
    margin-left: ${spacing.xLarge};

    ${media.medium`
      margin-left: ${spacing.xxLarge};
    `};

    ${media.large`
      margin-left: ${spacing.xxxLarge};
    `};
  }

  ${H1} {
    font-weight: ${fontWeights.medium};
    margin-bottom: 0;

    sub {
      font-size: 55%;
      bottom: 0;
      top: auto;
    }
  }

  small {
    color: ${white};
    display: block;
  }
`

const Step6Flavours = styled.div`
  min-width: 360px;
  max-width: 370px;
  margin-left: auto;
  margin-right: auto;

  ${media.large`
    max-width: 400px;
  `};

  ${media.xLarge`
    max-width: 440px;
  `};
`

const Step6FlavoursList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -${stripUnit(spacing.small) * 1.5 + 'px'};
`

const Step6Flavour = styled.div`
  padding: ${spacing.small};
  width: 20%;
  pointer-events: auto;

  ${media.xLarge`
    padding: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};

  img {
    width: 100%;
  }
`

const Step7Section = styled(Section)`
  background-image: url('${process.env.BASE_PATH}/images/products/epod/customize-background.jpg');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
`

const Step7Content = styled(Content)`
  position: absolute;
  top: 17.5%;
  left: 0;
  width: 100%;

  ${mediaDown.medium`
    padding: 0 10%;
  `};

  ${media.medium`
    top: 25%;
    left: 68%;
    width: 270px;
    padding-top: ${spacing.xLarge};
  `};

  ${media.large`
    width: 320px;
    left: 62%;
    padding-top: 56px;
  `};

  ${media.xLarge`
    width: 340px;
    left: 60%;
    padding-top: 64px;
  `};

  .fp-viewing-step-7 & {
    opacity: 1;
    visibility: visible;
  }
`

const Step7Subheading = styled(Subheading)`
  margin-bottom: 0.5em;
  text-align: center;

  ${media.medium`
    text-align: left;
  `};
`

const Step7Description = styled.p`
  position: static;

  ${media.medium`
    margin-bottom: 2em;
  `};

  ${media.large`
    margin-bottom: 2.5em;
  `};
`

const Step7Links = styled.div`
  display: flex;

  ${mediaDown.medium`
    justify-content: center;
  `};

  a {
    text-transform: uppercase;
    text-decoration: none;
    margin-right: ${spacing.large};

    &:last-child {
      margin-right: 0;
    }

    ${media.small`
      margin-right: ${spacing.xLarge};
    `};

    ${media.medium`
      margin-right: ${spacing.large};
    `};

    ${media.large`
      margin-right: ${spacing.xLarge};
    `};

    ${media.xLarge`
      margin-right: ${spacing.xxLarge};
    `};
  }
`

const Step7Figure = styled.img`
  width: 180px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${spacing.xLarge};
  pointer-events: none;

  ${media.small`
    width: 200px;
    margin-top: ${spacing.large};
    margin-bottom: ${spacing.xxLarge};
  `};

  ${media.medium`
    position: fixed;
    margin: 0;
    top: 50%;
    left: 45%;
    transform: translate3d(-50%, -50%, 0);
    width: 260px;
    z-index: 10;
    opacity: 0;
    visibility: hidden;
    transition: all ${SLIDESHOW_SPEED} ease-in-out;

    .fp-viewing-step-7 & {
      opacity: 1;
      visibility: visible;
    }
  `};

  ${media.large`
    width: 280px;
  `};

  ${media.xLarge`
    width: 17.5vw;
  `};
`

const SWATCHES = [
  {
    hex: '#000',
    name: 'black',
    active: true
  },
  {
    hex: '#a2acab',
    name: 'silver',
    active: false
  },
  {
    hex: '#f5c9c0',
    name: 'rose-gold',
    active: false
  },
  {
    hex: '#D5A73E',
    name: 'gold',
    active: false
  },
  {
    hex: '#D72A3D',
    name: 'red',
    active: false
  },
  {
    hex: '#407896',
    name: 'blue',
    active: false
  },
  {
    hex: '#3B9B94',
    name: 'aqua',
    active: false
  }
]

// const pluginWrapper = () => {}

const EpodSlideshow = () => {
  const [swatches, setSwatches] = useState(SWATCHES)
  const [expanded, setExpanded] = useState(false)
  const { province } = useContext(NativeAuthContext)

  const handleSwatchSelect = index => {
    const upToDateSwatches = [...swatches]

    upToDateSwatches.forEach((swatch, i) => {
      upToDateSwatches[i] = { ...swatch, active: false }
    })

    upToDateSwatches[index] = { ...upToDateSwatches[index], active: true }
    setSwatches([...upToDateSwatches])
  }

  const currentSwatch = swatches.find(swatch => swatch.active)

  const pluginWrapper = () => {
    /* eslint-disable no-undef */
    require('fullpage.js/vendors/scrolloverflow.min.js')
    require('scripts/fullpage.fadingEffect.min.js')
    /* eslint-enable no-undef */
  }

  return (
    <>
      <Portal selector="misc">
        <FixedHeader>
          <Header transparent />
        </FixedHeader>
      </Portal>
      <Container>
        <Details isDark price="9.99" color={yellow} route="/buy-online/epod-devices" hidePrice>
          <MenuWrap>
            <SlideshowMenu
              isDark
              color={white}
              items={[
                {
                  label: 'Buy now',
                  anchor: 'step-1'
                },
                {
                  label: 'Overview',
                  anchor: 'step-2'
                },
                {
                  label: 'Flavours',
                  anchor: 'step-6'
                },
                {
                  label: 'Personalize',
                  anchor: 'step-7'
                }
              ]}
            />
          </MenuWrap>
        </Details>
        <ScrollConsumer>
          {({ goToAnchor, handleCategoryChange }) => {
            return (
              <ReactFullpage
                licenseKey="AFAC473E-FFB74E20-B48212C5-37B5CDB8"
                fadingEffect
                fadingEffectKey={process.env.GATSBY_FULLPAGE_FADING_EFFECT_KEY}
                scrollOverflow={true}
                pluginWrapper={pluginWrapper}
                onLeave={(origin, destination) => {
                  const anchor = `#${destination.anchor}`
                  goToAnchor(anchor)
                  handleCategoryChange(anchor)
                }}
                render={({ fullpageApi }) => {
                  return (
                    <ReactFullpage.Wrapper>
                      <Section className="section" data-anchor="step-1">
                        <div
                          css={`
                            display: flex;
                            flex-direction: column;
                            height: 100%;
                            width: 100%;
                          `}
                        >
                          <HealthWarning />
                          <Portal selector={'misc'}>
                            <ShopLink className="u-hide-medium-up" onClick={() => fullpageApi.moveTo(1)}>
                              Click to shop
                            </ShopLink>
                          </Portal>

                          <div
                            css={`
                              width: 100%;
                              position: relative;
                              flex: 1;
                            `}
                          >
                            <Step1HeaderWrap>
                              <Header transparent />
                            </Step1HeaderWrap>
                            <Step1Content>
                              <Step1Heading>VUSE ePod</Step1Heading>
                              <Step1ContentInner>
                                <Step1List>
                                  <Step1ListItem>
                                    <ProductCard
                                      slug="bundle"
                                      name="Starter Bundle"
                                      price="$14.99"
                                      colors={['#555455', '#343434', '#F6CFCF', '#D8D8D8', '#D5A73E']}
                                      features={
                                        <span>
                                          1 ePod | 1 Pack of pods{' '}
                                          <span className="u-hide-medium-down">
                                            <br /> Customise your bundle by selecting 1 device colour and 1 pack of pods
                                          </span>
                                        </span>
                                      }
                                      link="/buy-online/epod-starter-bundle/"
                                    />
                                  </Step1ListItem>
                                  <Step1ListItem>
                                    <ProductCard
                                      slug="solo"
                                      name="Solo Device"
                                      price="$9.99"
                                      colors={['#555455', '#343434', '#F6CFCF', '#D8D8D8', '#D5A73E']}
                                      features={<span>1 ePod | 1 Charger</span>}
                                      link="/buy-online/epod-devices/"
                                    />
                                  </Step1ListItem>
                                  <Step1ListItem>
                                    <ProductCard
                                      slug="engravable"
                                      name="Engravable ePod"
                                      price="$14.99"
                                      colors={['#555455']}
                                      features={
                                        <span>
                                          1 ePod | 1 Charger{' '}
                                          <span className="u-hide-medium-down">
                                            <br /> Laser engraved device with icons, text, and patterns
                                          </span>
                                        </span>
                                      }
                                      link="/buy-online/engravable/"
                                    />
                                  </Step1ListItem>
                                  <Step1ListItem>
                                    <ProductCard
                                      slug="frequency"
                                      name="Frequency Collection"
                                      price="$9.99"
                                      colors={['#000', '#555455', '#F6CFCF']}
                                      features={
                                        <span>
                                          1 ePod | 1 Charger{' '}
                                          <span className="u-hide-medium-down">
                                            <br /> The new limited edition frequency collection for ePod
                                          </span>
                                        </span>
                                      }
                                      link="/buy-online/frequency-epod-devices/"
                                    />
                                  </Step1ListItem>
                                </Step1List>
                              </Step1ContentInner>
                              <Step1DownArrow onClick={() => fullpageApi.moveTo(2)}>
                                <Icon icon={<DownChevronLarge />} size="medium" />
                              </Step1DownArrow>
                            </Step1Content>
                          </div>
                        </div>
                      </Section>
                      {/* Step 2 */}
                      <Section className="section" data-anchor="step-2">
                        <Portal selector={'misc'}>
                          <Fade />
                          <Step2DeviceWrap className="u-hide-medium-down">
                            <img
                              src={`${
                                process.env.BASE_PATH
                              }/images/products/epod/device-${currentSwatch.name.toLowerCase()}.png`}
                              alt="Vuse ePod Starter Kit"
                            />
                          </Step2DeviceWrap>
                        </Portal>
                        <Step2SwatchWrap className="u-hide-medium-up">
                          <SwatchList onClick={handleSwatchSelect} swatches={swatches} isDark />
                        </Step2SwatchWrap>
                        <Step2Content>
                          <Step2Heading>Vuse ePod</Step2Heading>
                          <Step2MobileDevice
                            src={`${
                              process.env.BASE_PATH
                            }/images/products/epod/device-mobile-${currentSwatch.name.toLowerCase()}.png`}
                            className="u-hide-medium-up"
                            alt="Vuse ePod Starter Kit"
                          />
                          <Step2Subheading as="h2">Sleek and stylish. The perfect puff is here.</Step2Subheading>
                          <Step2SwatchWrap className="u-hide-medium-down">
                            <SwatchList onClick={handleSwatchSelect} swatches={swatches} isDark />
                          </Step2SwatchWrap>
                        </Step2Content>
                      </Section>

                      {/* Step 3 */}
                      <Section className="section" data-anchor="step-3">
                        <Step3Content>
                          <Step3ContentWrap>
                            <Step3MobileDevice>
                              <Step3Line className="u-hide-medium-up" />
                              <img
                                src={`${
                                  process.env.BASE_PATH
                                }/images/products/epod/device-mobile-${currentSwatch.name.toLowerCase()}.png`}
                                alt="Vuse ePod Starter Kit"
                              />
                            </Step3MobileDevice>

                            <Subheading>QUIET. SMOOTH. SATISFYING.</Subheading>
                            <p>
                              Not only is the ePod sleek and stylish, it is also designed to deliver a quiet, smooth and
                              satisfying taste experience.
                            </p>
                          </Step3ContentWrap>
                        </Step3Content>
                        <Portal selector="misc">
                          <Step3Line className="u-hide-medium-down" />
                        </Portal>
                      </Section>

                      {/* Step 4 */}
                      <Section className="section" data-anchor="step-4">
                        <Step4Content>
                          <Step4ContentWrap>
                            <Step4MobileDevice>
                              <Step4Line className="u-hide-medium-up" />
                              <img
                                src={`${
                                  process.env.BASE_PATH
                                }/images/products/epod/device-mobile-${currentSwatch.name.toLowerCase()}.png`}
                                alt="Vuse ePod Starter Kit"
                              />
                            </Step4MobileDevice>

                            <Subheading>ALL-DAY USE*</Subheading>
                            <p>
                              When fully charged, the Vuse ePod will deliver approximately 190 puffs, while a single
                              cartridge will give you approximately 275 puffs.
                            </p>
                            <p>
                              <small>*based on a typical usage.</small>
                            </p>
                          </Step4ContentWrap>
                        </Step4Content>
                        <Portal selector="misc">
                          <Step4Line className="u-hide-medium-down" />
                        </Portal>
                      </Section>

                      {/* Step 5 */}
                      <Section className="section" data-anchor="step-5">
                        <Portal selector={'misc'}>
                          <Step5Size className="u-hide-medium-down">
                            <Step5SizeLabel>10.3 cm</Step5SizeLabel>
                          </Step5Size>
                          <Step5Line className="u-hide-medium-down" />
                        </Portal>

                        <Step5Content>
                          <Step5MobileDeviceWrap className="u-hide-medium-up">
                            <img
                              src={`${
                                process.env.BASE_PATH
                              }/images/products/epod/device-${currentSwatch.name.toLowerCase()}.png`}
                              alt="Vuse ePod Starter Kit"
                            />
                            <Step5MobileSizeLabel>10.3 cm</Step5MobileSizeLabel>
                            <Step5MobileLine />
                          </Step5MobileDeviceWrap>
                          <Subheading>
                            PERFECTLY
                            <br /> POCKET-SIZED
                          </Subheading>
                          <p>Compact design to fit in your pocket</p>
                        </Step5Content>
                      </Section>

                      {/* Step 6 */}
                      <Section className="section" data-anchor="step-6">
                        <Portal selector={'misc'}>
                          <Step6DeviceWrap className="u-hide-medium-down">
                            <img
                              src={`${
                                process.env.BASE_PATH
                              }/images/products/epod/device-open-${currentSwatch.name.toLowerCase()}.png`}
                              alt="Vuse ePod Starter Kit"
                            />
                          </Step6DeviceWrap>
                        </Portal>
                        <Step6Content>
                          <Step6ContentInner>
                            <Step6DeviceWrap className="u-hide-medium-up">
                              <img
                                src={`${
                                  process.env.BASE_PATH
                                }/images/products/epod/device-open-${currentSwatch.name.toLowerCase()}.png`}
                                alt="Vuse ePod Starter Kit"
                              />
                            </Step6DeviceWrap>
                            <Subheading>A NEW WORLD OF FLAVOURS.</Subheading>
                            <p
                              css={`
                                font-weight: ${fontWeights.medium};
                              `}
                            >
                              ePod pods deliver intense satisfaction. Available in 20 flavours and 4 nicotine strengths.
                            </p>
                            <Step6Flavours className="u-hide-medium-down">
                              <Step6FlavoursList>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/cool-peppermint.png`}
                                    data-tip="Cool Peppermint"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/rich-tobacco.png`}
                                    data-tip="Rich Tobacco"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/strawberry.png`}
                                    data-tip="Strawberry"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/blood-orange.png`}
                                    data-tip="Blood Orange"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/blueberry.png`}
                                    data-tip="Blueberry"
                                  />
                                </Step6Flavour>

                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/aromatic-tobacco.png`}
                                    data-tip="Aromatic Tobacco"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/cucumber.png`}
                                    data-tip="Cucumber"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/lychee.png`}
                                    data-tip="Lychee"
                                  />
                                </Step6Flavour>

                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/tobacco.png`}
                                    data-tip="Golden Tobacco"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/mango.png`}
                                    data-tip="Mango"
                                  />
                                </Step6Flavour>

                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/berry.png`}
                                    data-tip="Berry Blast"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/vanilla.png`}
                                    data-tip="Vanilla"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/smooth-mint.png`}
                                    data-tip="Smooth Mint"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/passionfruit.png`}
                                    data-tip="Passionfruit"
                                  />
                                </Step6Flavour>

                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/smooth-tobacco.png`}
                                    data-tip="Smooth Tobacco"
                                  />
                                </Step6Flavour>

                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/peach.png`}
                                    data-tip="Peach"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/lemon-berry.png`}
                                    data-tip="Lemon Berry"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/watermelon.png`}
                                    data-tip="Watermelon"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/mint.png`}
                                    data-tip="Polar Mint"
                                  />
                                </Step6Flavour>
                                <Step6Flavour>
                                  <img
                                    src={`${process.env.BASE_PATH}/images/products/epod/droplets/clear.png`}
                                    data-tip="Clear"
                                  />
                                </Step6Flavour>
                              </Step6FlavoursList>
                            </Step6Flavours>
                            {/* <ReactTooltip effect="solid" className="tooltip" /> */}
                            <Step6Strength>
                              {province === 'BC' ? (
                                <Strength>
                                  <H1 as="h2">
                                    1.6<sub>%</sub>
                                  </H1>
                                  <small>
                                    nicotine
                                    <br />
                                    strength
                                  </small>
                                </Strength>
                              ) : (
                                <>
                                  <Strength>
                                    <H1 as="h2">
                                      0<sub>%</sub>
                                    </H1>
                                    <small>
                                      nicotine
                                      <br />
                                      strength
                                    </small>
                                  </Strength>
                                  <Strength>
                                    <H1 as="h2">
                                      1.6<sub>%</sub>
                                    </H1>
                                    <small>
                                      nicotine
                                      <br />
                                      strength
                                    </small>
                                  </Strength>
                                  <Strength>
                                    <H1 as="h2">
                                      3<sub>%</sub>
                                    </H1>
                                    <small>
                                      nicotine
                                      <br />
                                      strength
                                    </small>
                                  </Strength>
                                  <Strength>
                                    <H1 as="h2">
                                      5<sub>%</sub>
                                    </H1>
                                    <small>
                                      nicotine
                                      <br />
                                      strength
                                    </small>
                                  </Strength>
                                </>
                              )}
                            </Step6Strength>
                            <Step6Button
                              onClick={() => {
                                setExpanded(true)
                              }}
                              className="u-hide-medium-up"
                            >
                              View flavours
                            </Step6Button>
                            <Link to="/buy-online/vype-epod-vpro-cartridges" passHref>
                              <StyledLink className="u-hide-medium-down">Shop flavours</StyledLink>
                            </Link>
                          </Step6ContentInner>
                        </Step6Content>
                        {expanded && (
                          <Portal selector="misc">
                            <Step6MobileContent className="u-hide-medium-up">
                              <Step6MobileContentInner>
                                <FlavourSlider />
                                <Link to="/buy-online/vype-epod-vpro-cartridges" passHref>
                                  <StyledLink>Shop flavours</StyledLink>
                                </Link>
                                <Step6MobileClose
                                  onClick={() => {
                                    setExpanded(false)
                                  }}
                                >
                                  <Icon icon={<CloseIcon />} />
                                </Step6MobileClose>
                              </Step6MobileContentInner>
                            </Step6MobileContent>
                          </Portal>
                        )}
                      </Section>

                      {/* Step 7 */}
                      <Step7Section className="section" data-anchor="step-7">
                        <Step7Content>
                          <Step7Subheading>
                            PERSONALIZE <br className="u-hide-medium-down" />
                            YOUR ePod
                          </Step7Subheading>
                          <Step7Description>
                            With a vast choice of printed skins and laser engraving designs for your device
                          </Step7Description>

                          {/* Desktop links */}
                          <Step7Links className="u-hide-medium-down">
                            <StyledLink to="/buy-online/vype-epod-skins">Shop skins</StyledLink>
                            <StyledLink to="/buy-online/engravable">Engrave device</StyledLink>
                          </Step7Links>

                          <Step7Figure
                            src={`${process.env.BASE_PATH}/images/products/epod/engraving.png`}
                            className="u-hide-medium-up"
                          />

                          {/* Mobile links */}
                          <Step7Links className="u-hide-medium-up">
                            <StyledLink to="/buy-online/vype-epod-skins">Shop skins</StyledLink>
                            <StyledLink to="/buy-online/engravable">Engrave device</StyledLink>
                          </Step7Links>
                        </Step7Content>
                        <Portal selector="misc">
                          <Step7Figure
                            src={`${process.env.BASE_PATH}/images/products/epod/engraving.png`}
                            className="u-hide-medium-down"
                          />
                        </Portal>
                      </Step7Section>
                    </ReactFullpage.Wrapper>
                  )
                }}
              />
            )
          }}
        </ScrollConsumer>
      </Container>
    </>
  )
}

export default EpodSlideshow
